import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Row, Col, Container, Modal, ModalHeader, ModalBody, ModalFooter, Input, Button } from "reactstrap";
import { HeaderTitle, PrimaryButton, Colors } from "../../../utils/budget-tool-specific";
import { useParams } from "react-router-dom";
import PassportGrid from "./passportGrid";
import { GetDB } from "../../../utils/database/firebase-budget-tool";
import { listenToDocument, updateDocumentProperty } from "../../../utils/firestore-functions";
import { comparePassports } from "./comparePassports";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import PassportComparison from "./passportComparison";

const PassportEdit = (props) => {
    let { experience } = useParams();
    console.log('PassportEditComponent - props:', props);
    const budgetDB = GetDB();
    const [passportData, setPassportData] = useState(null);
    const [savedModal, setSavedModal] = useState(false);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleInput, setTitleInput] = useState("");
    const [copiedModal, setCopiedModal] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [comparisonResults, setComparisonResults] = useState([]);
    const [countries, setCountries] = useState([]);
    const [visaWeights, setVisaWeights] = useState({
        noVisa: 1000,
        visaOnArrival: 100,
        eVisa: 10,
        paperVisa: 1,
        restricted: 0
    });

    useEffect(() => {
        var unsubscribe = listenToDocument(budgetDB, "passportGuides", experience, SetPassport);
        return unsubscribe;
    }, [experience]);

    useEffect(() => {
        // Get countries data from the new structure
        const unsubscribeCountries = onSnapshot(doc(budgetDB, 'data', 'countries_metadata'), async (metadataDoc) => {
            if (!metadataDoc.exists()) {
                console.log('No countries metadata found');
                return;
            }

            const metadata = metadataDoc.data();
            const regions = metadata.regions || [];

            // Fetch all region documents
            const regionPromises = regions.map(region => 
                getDoc(doc(budgetDB, 'data', `countries_${region.toLowerCase()}`))
            );

            try {
                const regionDocs = await Promise.all(regionPromises);
                const allCountries = [];

                regionDocs.forEach((regionDoc) => {
                    if (regionDoc.exists()) {
                        const regionData = regionDoc.data();
                        Object.entries(regionData.countries).forEach(([countryId, countryData]) => {
                            allCountries.push({
                                id: countryId,
                                ...countryData
                            });
                        });
                    }
                });

                // Sort countries alphabetically by name
                const sortedCountries = allCountries.sort((a, b) => a.country.localeCompare(b.country));
                console.log('Fetched countries data:', sortedCountries);
                setCountries(sortedCountries);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        });

        // Get visa weights
        const unsubscribeWeights = onSnapshot(doc(budgetDB, 'Settings', 'visaWeights'), (docSnapshot) => {
            if (docSnapshot.exists()) {
                setVisaWeights(docSnapshot.data());
            }
        });

        return () => {
            unsubscribeCountries();
            unsubscribeWeights();
        };
    }, []);

    const SetPassport = (passport) => {
        if (passport) {
            // Reconstruct the data structure
            const selectedCountries = (passport.selectedCountries || []).map(item => 
                typeof item === 'string' ? item : item.countryId
            );
            
            const travelerCounts = (passport.selectedCountries || []).reduce((acc, item) => {
                if (typeof item === 'object') {
                    acc[item.countryId] = item.travelers;
                }
                return acc;
            }, {});

            const weights = (passport.selectedCountries || []).reduce((acc, item) => {
                if (typeof item === 'object') {
                    acc[item.countryId] = item.weight;
                }
                return acc;
            }, {});

            console.log('Setting passport data:', {
                selectedCountries,
                travelerCounts,
                weights
            });

            setPassportData({
                ...passport,
                selectedCountries,
                travelerCounts,
                weights
            });
            setTitleInput(passport.title || "");
        } else {
            setPassportData(passport);
        }
    };

    const handleTitleChange = (e) => {
        setTitleInput(e.target.value);
    };

    const handleTitleBlur = () => {
        setIsEditingTitle(false);
        if (titleInput.trim() !== passportData.title) {
            setPassportData(prev => ({ ...prev, title: titleInput.trim() }));
        }
    };

    const handleTitleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleTitleBlur();
        }
    };

    const handleSaveChanges = async (changes) => {
        try {
            console.log('Saving passport changes:', changes);
            
            // Update the passport data in the state
            setPassportData(prev => ({
                ...prev,
                ...changes
            }));

            // Update the document in Firestore
            await updateDocumentProperty(
                budgetDB,
                "passportGuides",
                experience,
                changes
            );

            setSavedModal(true);
        } catch (error) {
            console.error('Error saving passport changes:', error);
        }
    };

    const backToGuideList = () => {
        window.location = "/budget-app/passport";
    };

    const saveForm = () => {
        if (passportData) {
            console.log('Saving passport data:', passportData);
            // Save the title
            updateDocumentProperty(budgetDB, "passportGuides", experience, "title", passportData.title);

            // Save the selected countries with their traveler counts and weights
            const selectedCountriesWithDetails = (passportData.selectedCountries || []).map(countryId => ({
                countryId: countryId,
                travelers: passportData.travelerCounts?.[countryId] || 1,
                weight: passportData.weights?.[countryId] || 1
            }));

            console.log('Saving selected countries with details:', selectedCountriesWithDetails);

            updateDocumentProperty(
                budgetDB,
                "passportGuides",
                experience,
                "selectedCountries",
                selectedCountriesWithDetails,
                () => setSavedModal(true)
            );
        }
    };

    const copyClientUrl = () => {
        const clientUrl = `${window.location.origin}/budget-app/passport/${experience}`;
        navigator.clipboard.writeText(clientUrl).then(() => {
            setCopiedModal(true);
        });
    };

    const handleCompare = () => {
        if (!passportData?.selectedCountries) return;
        const results = comparePassports(
            countries, 
            passportData.selectedCountries, 
            countries,
            passportData.travelerCounts || {},
            { maxCountries: 'all' }
        );
        setComparisonResults(results);
        setShowResults(true);
    };

    console.log('PassportEdit - experience:', experience, 'current path:', window.location.pathname);

    if (experience === undefined) {
        console.log('No experience found, redirecting to passport list');
        window.history.replaceState(null, "", "/budget-app/passport");
        return null;
    }

    console.log('Rendering PassportEdit with experience:', experience);

    return (
        <PageContainer>
            <HeaderTitle>
                {isEditingTitle ? (
                    <Input
                        type="text"
                        value={titleInput}
                        onChange={handleTitleChange}
                        onBlur={handleTitleBlur}
                        onKeyPress={handleTitleKeyPress}
                        autoFocus
                    />
                ) : (
                    <span onClick={() => setIsEditingTitle(true)}>
                        {passportData?.title || "Loading..."}
                    </span>
                )}
            </HeaderTitle>
            <DisclaimerText>
                Information is provided as guidance only and accurate at the time of publishing. Always check government websites and airline materials before booking and traveling.
            </DisclaimerText>
            <Row>
                <Col style={{textAlign: "left"}}>
                    <CompareButton 
                        onClick={handleCompare}
                        disabled={!passportData?.selectedCountries?.length}
                    >
                        Compare Selected Passports
                    </CompareButton>
                </Col>
                <Col style={{textAlign: "right"}}>
                    {!props.client && (
                        <PrimaryButton onClick={copyClientUrl} style={{marginRight: "10px"}}>
                            Copy Client URL
                        </PrimaryButton>
                    )}
                    <PrimaryButton onClick={saveForm}>
                        Save Changes
                    </PrimaryButton>
                </Col>
            </Row>
            {showResults && (
                <PassportComparison 
                    comparisonResults={comparisonResults}
                    onClose={() => setShowResults(false)}
                    visaWeights={visaWeights}
                    selectedPassports={passportData?.selectedCountries}
                    countries={countries}
                />
            )}
            <PassportGrid 
                passportData={passportData} 
                setPassportData={setPassportData}
                client={props.client}
            />
            <Modal isOpen={savedModal} toggle={() => setSavedModal(false)}>
                <ModalHeader toggle={() => setSavedModal(false)}>Success</ModalHeader>
                <ModalBody>
                    Changes saved successfully!
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setSavedModal(false)}>OK</Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={copiedModal} toggle={() => setCopiedModal(false)}>
                <ModalHeader toggle={() => setCopiedModal(false)}>Success</ModalHeader>
                <ModalBody>
                    Client URL copied to clipboard!
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setCopiedModal(false)}>OK</Button>
                </ModalFooter>
            </Modal>
        </PageContainer>
    );
};

export default PassportEdit;

const PageContainer = styled.div`
    padding: 30px 15px;
    height: 100%;
    box-sizing: border-box;
`;

const DisclaimerText = styled.div`
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 15px;
    margin: 15px 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
`;

const StyledContainer = styled(Container)`
    padding: 32px;
`;

const TitleInput = styled(Input)`
    font-size: 24px;
    font-weight: bold;
    color: ${Colors.primaryDark};
    border: 2px solid ${Colors.primaryAccent};
    padding: 8px 12px;
    margin-top: 30px;
    width: 100%;
    max-width: 600px;

    &:focus {
        box-shadow: none;
        border-color: ${Colors.primaryAccent};
    }
`;

const CompareButton = styled(PrimaryButton)`
    background: ${Colors.primaryAccent};
    color: ${Colors.primaryDark};
    font-size: 16px;
    font-weight: bold;
    padding: 12px 24px;
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
        background: ${Colors.primaryDark};
        color: ${Colors.primaryAccent};
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    &:disabled {
        background: #cccccc;
        color: #666666;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`; 